import { h } from 'preact'
import { useEffect } from 'preact/compat'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { CARD_FIELDS } from '../../../../constants/card'
import { useTranslations } from '../../../../hooks/useTranslations'
import { AUTOCOMPLETE_VALUES } from '../../../../constants/autocompleteValues'
import {
  getSelectionText,
  isAllowedNumberInputKey,
} from '../../../../helpers/common'
import styles from './ExpirationDate.module.scss'

const maxYear = new Date().getFullYear() - 2000 + 10
const minYear = new Date().getFullYear() - 2000

const ExpirationDate = ({
  register,
  errors,
  trigger,
  watch,
  setValue,
  setFocus,
  clearErrors,
}) => {
  const l10n = useTranslations()

  const reset = () => {
    setValue(CARD_FIELDS.expirationMonth, '', { shouldValidate: true })
    setValue(CARD_FIELDS.expirationYear, '', { shouldValidate: true })
  }

  const month = watch(CARD_FIELDS.expirationMonth)
  const year = watch(CARD_FIELDS.expirationYear)

  useEffect(() => {
    if (month?.length === 2 && !year?.length) {
      setFocus(CARD_FIELDS.expirationYear, { shouldSelect: true })
    }
  }, [month])

  // @HACK: OPP-2599. Trigger validation due to firefox autofill
  useEffect(async () => {
    if (month?.length === 2 && year?.length === 2) {
      await trigger(CARD_FIELDS.expirationMonth)
      await trigger(CARD_FIELDS.expirationYear)
    }
  }, [month, year])

  const dateFilled = (_, { expirationYear, expirationMonth }) => {
    const isYearMonthFilled = expirationYear?.length && expirationMonth?.length
    const isOnlyMonthFilled = !expirationYear?.length && expirationMonth?.length
    const isMonthFullyFilled = expirationMonth?.length === 2
    const minMonth =
      Number(expirationYear) === minYear ? new Date().getMonth() + 1 : 1
    const maxMonth = 12
    const isCurrentMonthAllowed =
      minMonth <= expirationMonth && maxMonth >= expirationMonth

    const isCurrentYearAllowed =
      Number(expirationYear) >= minYear && Number(expirationYear) <= maxYear

    if (
      isYearMonthFilled &&
      (!isCurrentMonthAllowed || !isCurrentYearAllowed)
    ) {
      return false
    }

    if (!isMonthFullyFilled) {
      return false
    }

    if (isOnlyMonthFilled) {
      setFocus(CARD_FIELDS.expirationYear, { shouldSelect: false })
    } else if (!expirationMonth?.length) {
      setFocus(CARD_FIELDS.expirationMonth, { shouldSelect: false })
    }

    clearErrors(CARD_FIELDS.expirationYear)
    clearErrors(CARD_FIELDS.expirationMonth)
    return true
  }

  const handleKeyPress = e => {
    const value = e?.target?.value

    const lengthValidation = value?.length >= 2 && getSelectionText() !== value

    if ((lengthValidation && /\d/.test(e.key)) || !isAllowedNumberInputKey(e)) {
      e.preventDefault()
    }
  }

  const hasErrors =
    Boolean(errors[CARD_FIELDS.expirationMonth]) ||
    Boolean(errors[CARD_FIELDS.expirationYear])

  const inputClasses = classNames([
    {
      error: hasErrors,
    },
    'input',
    'input-text',
    'payment-form__field',
    'tenant-overrides-expire-month',
    styles.expirationDate,
  ])

  const dateClasses = classNames(styles.expirationDateInput, 'tenant-overrides')

  return (
    <label className={inputClasses} data-testid="expiration-date" id={`label-${CARD_FIELDS.expirationMonth}`}>
      <span className="label">
        {l10n.t('Expiry date')} ({l10n.t('MM/YY')})
      </span>
      <div className={`group ${styles.expirationDateGroup}`}>
        <input
          data-testid="expire-month"
          type="number"
          id={CARD_FIELDS.expirationMonth}
          min={1}
          max={12}
          maxLength={2}
          autoComplete={AUTOCOMPLETE_VALUES.CARD_EXP_MONTH}
          inputMode="numeric"
          className={dateClasses}
          onKeyDown={handleKeyPress}
          {...register(CARD_FIELDS.expirationMonth, {
            required: true,
            maxLength: 2,
            max: 12,
            min: 1,
            validate: {
              dateFilled,
            },
          })}
        />
        {(month || year) && (
          <span className={styles.expirationDateSeparator}> / </span>
        )}
        <input
          id={CARD_FIELDS.expirationYear}
          data-testid="expire-year"
          controls={false}
          type="number"
          min={minYear}
          max={maxYear}
          maxLength={2}
          autoComplete={AUTOCOMPLETE_VALUES.CARD_EXP_YEAR}
          inputMode="numeric"
          className={dateClasses}
          onKeyDown={handleKeyPress}
          onClick={() => {
            if (!month && !year) {
              setFocus(CARD_FIELDS.expirationMonth, { shouldSelect: true })
            }
          }}
          {...register(CARD_FIELDS.expirationYear, {
            required: true,
            maxLength: 2,
            max: maxYear,
            min: minYear,
            validate: {
              dateFilled,
            },
          })}
        />
      </div>
      {hasErrors && (
        <p className="message" data-testid="error-message">
          <span className="icon xsmall"></span>
          {l10n.t('Please enter the date exactly as it appears on your card')}
        </p>
      )}
    </label>
  )
}

ExpirationDate.propTypes = {
  watch: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  setFocus: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
}

export default ExpirationDate
